import {
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { SupplierWithId } from '../../models/supplier.model';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { useTranslation } from 'react-i18next';
import { createSupplier } from '../../slices/suppliers/suppliersSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useEmailField } from '../../common/hooks/useEmailField';
import { useErrorMessage } from '../../common/hooks/useErrorMessage';

export const CreateSupplierProfile: React.FC = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const company = useAppSelector(selectAllCompanies)[0];
  const [formData, setFormData] = useState<Partial<SupplierWithId>>({
    company: company.id,
    name: '',
    registration_number: '',
    supplier_email: '',
    website: '',
    physical_address: '',
    postal_address: '',
    industry: '',
    supplier_type: '',
    description: '',
  });
  const [fieldsErrors, setFieldsErrors] = useState<Record<string, string[]> | null>(null);
  const [pendingStatus, setPendingStatus] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Lower cases what is typed and applies the shared basic email validation.
  const emailField = useEmailField({
    value: formData.supplier_email,
    onChange: (value: string) => setFormData((prev) => ({ ...prev, supplier_email: value })),
  });

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const errors: Record<string, string[]> = {};
    if (!formData.name) {
      errors.name = ['Supplier name is required'];
    }
    // Optional field, but it has to be a valid address when filled in.
    // The message is rendered by the field itself, so it is not added to `errors`.
    const isEmailValid = emailField.validate();

    if (Object.keys(errors).length > 0 || !isEmailValid) {
      setFieldsErrors(Object.keys(errors).length > 0 ? errors : null);
      return;
    }
    setPendingStatus(true);

    dispatch(createSupplier(formData))
      .unwrap()
      .then((res: SupplierWithId) => {
        toast.success('Supplier profile created successfully!');
        setFormData({
          company: company.id,
          name: '',
          registration_number: '',
          website: '',
          physical_address: '',
          postal_address: '',
          industry: '',
          supplier_type: '',
          description: '',
        });
        setFieldsErrors(null);
        navigate(ROUTES.SUPPLIER_PROFILES + '/' + res.id);
      })
      .catch((error: any) => {
        setFieldsErrors(error.response.data);
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  const supplierTypeOptions = [
    'Business Partner',
    'Client',
    'Consultant',
    'Contractor',
    'Manufacturer',
    'Supplier of goods and services',
  ];

  return (
    <>
      <AppHeader title={t('buttons.createSupplier')} />
      <div className='profile-details-wrapper'>
        <form onSubmit={handleSubmit}>
          <Card sx={{ padding: '16px', marginBottom: '1rem' }}>
            <Grid component='div' container className='form-controls' spacing={2}>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.supplierName')} *`}
                  onChange={handleInputChange}
                  name='name'
                  value={formData.name}
                  error={Boolean(fieldsErrors?.name)} // Add this line
                  helperText={fieldsErrors?.name?.join('.')}
                />
              </Grid>

              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.supplierRegistrationNumber')}`}
                  onChange={handleInputChange}
                  name='registration_number'
                  value={formData.registration_number}
                />
                {Boolean(fieldsErrors?.registration_number) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.registration_number.join('.')}
                  </FormHelperText>
                )}
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.supplierEmail')}`}
                  name='supplier_email'
                  {...emailField.fieldProps}
                  error={emailField.hasError || Boolean(fieldsErrors?.supplier_email)}
                  helperText={emailField.errorMessage || undefined}
                />
                {Boolean(fieldsErrors?.supplier_email) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.supplier_email.join('.')}
                  </FormHelperText>
                )}
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('labels.website')}
                  onChange={handleInputChange}
                  name='website'
                  value={formData.website}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.supplierDescription')}`}
                  onChange={handleInputChange}
                  name='description'
                  value={formData.description}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.physicalAddress')}`}
                  onChange={handleInputChange}
                  name='physical_address'
                  value={formData.physical_address}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.postalAddress')}`}
                  onChange={handleInputChange}
                  name='postal_address'
                  value={formData.postal_address}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.industry')}`}
                  onChange={handleInputChange}
                  name='industry'
                  value={formData.industry}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth variant='outlined'>
                  <InputLabel htmlFor='supplierType'>{t('labels.type')}</InputLabel>
                  <Select
                    id='createSupplierProfileSupplierTypeSelect'
                    fullWidth
                    aria-label='supplierType'
                    value={formData.supplier_type}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, supplier_type: e.target.value }))
                    }
                    displayEmpty
                    slotProps={{
                      input: { id: 'supplierType' },
                    }}
                  >
                    {supplierTypeOptions.map((option) => (
                      <MenuItem
                        id={`createSupplierProfileSupplierTypeOptionMenuItem_${option}`}
                        key={option}
                        value={option}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
          <Grid
            component='div'
            container
            style={{ justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}
          >
            <Button
              id='createSupplierProfileGoToSupplierProfilesBtn'
              type='button'
              variant='contained'
              size='medium'
              className='btn-default btn-action-basic btn-transparent-initial btn-cancel'
              onClick={() => {
                navigate(ROUTES.SUPPLIER_PROFILES);
              }}
            >
              {`${t('buttons.goBack')}`}
            </Button>
            <Button
              id='createSupplierProfileSubmitBtn'
              type='submit'
              variant='contained'
              disabled={pendingStatus}
              className='btn-default btn-action-basic'
            >
              {pendingStatus ? 'Submitting...' : 'Submit'}
            </Button>
          </Grid>
        </form>
      </div>
    </>
  );
};
